export const organizationPaymentStatuses = ['free', 'paid'] as const;
export type OrganizationPaymentStatus = (typeof organizationPaymentStatuses)[number];

export type OrganizationMetadata = {
    name: string;
    description: string;
    brandingPrimaryColor?: string;
    brandingSecondaryColor?: string;
    sessionTimeoutMinutes?: number | null;
};

export type Organization = {
    organizationId: string;
    ownerUserId: string;
    metadata: OrganizationMetadata;
    paymentStatus: OrganizationPaymentStatus;
    isPubliclyListed?: boolean;
    hubspotCompanyId?: string;
};

export const organizationUserRoles = ['owner', 'admin', 'agent'] as const;
export type OrganizationUserRole = (typeof organizationUserRoles)[number];

export type OrganizationUser = {
    organizationUserId: string;
    organizationId: string;
    userId: string;
    role: OrganizationUserRole;
    isBillingAdmin?: boolean;
};

export type OrganizationGroupMetadata = {
    name: string;
    description?: string;
};

export type OrganizationGroup = {
    organizationGroupId: string;
    organizationId: string;
    metadata: OrganizationGroupMetadata;
};

export type OrganizationGroupUser = {
    organizationGroupUserId: string;
    organizationGroupId: string;
    organizationUserId: string;
};

export type OrganizationGroupMedicalProfile = {
    organizationGroupMedicalProfileId: string;
    organizationId: string;
    organizationGroupId: string;
    medicalProfileId: string;
};

export type OrganizationGroupClientRecord = {
    organizationGroupClientRecordId: string;
    organizationId: string;
    organizationGroupId: string;
    organizationClientRecordId: string;
};

export type InvitationDefinitionInputConsentCheckbox = {
    type: 'consent-checkbox';
    label: string;
};

export type InvitationDefinitionInputFile = {
    type: 'file';
    fileInputId: string;
    isRequired: boolean;
    description: string;
    name: string;
};

export type InvitationDefinitionInput = InvitationDefinitionInputConsentCheckbox | InvitationDefinitionInputFile;

export type InvitationDefinitionFile = {
    fileId: string;
    description: string;
    name: string;
    contentType?: string;
};

export type OrganizationInvitationDefinition = {
    organizationInvitationDefinitionId: string;
    organizationId: string;
    message: string | null;
    inputs: InvitationDefinitionInput[];
    files: InvitationDefinitionFile[];
    name: string;
};

export type OrganizationClientInvitationFile = InvitationDefinitionFile;
export type OrganizationClientInvitationInput = InvitationDefinitionInput;

export type OrganizationClientInvitation = {
    organizationClientInvitationId: string;
    organizationId: string;
    organizationInvitationDefinitionId: string;
    organizationClientRecordId: string;
    destinationOrganizationGroupIds?: string[];
    inviterOrganizationUserId: string;
    sentDateTime: string;
    expirationDateTime?: string;
    clientOrganizerEmail: string;
    clientOrganizerUserId?: string;
    clientOrganizerFirstName: string;
    clientOrganizerLastName: string;
    clientFirstName: string;
    clientLastName: string;
    clientDOB: string;
    inputs: OrganizationClientInvitationInput[];
    files: OrganizationClientInvitationFile[];
    message: string | null;
};

export type OrganizationUserInvitation = {
    organizationUserInvitationId: string;
    organizationId: string;
    inviteeRole: OrganizationUserRole;
    inviteeEmail: string;
    inviteeUserId?: string;
    inviteeFirstName: string;
    inviteeLastName: string;
    message: string | null;
};

export type OrganizationClientRecordClientOrganizerDetails = {
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    phoneNumber?: string;
};

export type OrganizationClientRecordClientDetails = {
    firstName: string;
    lastName: string;
    preferredName?: string;
    dob: string;
};

export type OrganizationClientRecordInvitationFileInput = OrganizationClientInvitationFile & {
    fileInputId: string;
};

export type OrganizationClientRecordInvitationDetails = {
    sentDateTime: string;
    inputs: OrganizationClientInvitationInput[];
    files: OrganizationClientInvitationFile[];
    fileInputs?: OrganizationClientRecordInvitationFileInput[];
    message: string | null;
};

export const organizationClientStatuses = [
    'not-invited',
    'archived',
    'invited',
    'invitation-rejected',
    'active',
    'inactive',
] as const;
export type OrganizationClientRecordStatus = (typeof organizationClientStatuses)[number];

export type OrganizationClientRecordBaseFields = {
    organizationClientRecordId: string;
    organizationId: string;
    createdDateTime: string;
    createdByOrganizationUserId: string;
    clientOrganizerDetails: Partial<OrganizationClientRecordClientOrganizerDetails>;
    clientDetails: Partial<OrganizationClientRecordClientDetails>;
    clientMedicalProfileId?: string;
    stripeCustomerId?: string;
};

export type OrganizationClientRecordNotInvitedFields = {
    status: 'not-invited';
};

export type OrganizationClientRecordArchivedFields = {
    status: 'archived';
};

export type OrganizationClientRecordInvitedFields = {
    status: 'invited';
    clientDetails: OrganizationClientRecordClientDetails;
    clientOrganizerDetails: OrganizationClientRecordClientOrganizerDetails;
    provisionedMedicalProfileId?: string;
};

export type OrganizationClientRecordInvitationRejectedFields = {
    status: 'invitation-rejected';
    clientDetails: OrganizationClientRecordClientDetails;
    clientOrganizerDetails: OrganizationClientRecordClientOrganizerDetails;
    invitationDetails: OrganizationClientRecordInvitationDetails & {
        rejectedDateTime: string;
    };
    provisionedMedicalProfileId?: string;
};

export type OrganizationClientRecordActiveFields = {
    status: 'active';
    startDateTime: string;
    clientDetails: OrganizationClientRecordClientDetails;
    clientOrganizerDetails: OrganizationClientRecordClientOrganizerDetails;
    invitationDetails: OrganizationClientRecordInvitationDetails;
    clientMedicalProfileId: string;
    clientOrganizerUserId: string;
    expirationDateTime?: string;
    provisionedMedicalProfileId?: string;
};

export type OrganizationClientRecordInactiveFields = {
    status: 'inactive';
    startDateTime: string;
    endDateTime: string;
    clientDetails: OrganizationClientRecordClientDetails;
    clientOrganizerDetails: OrganizationClientRecordClientOrganizerDetails;
    invitationDetails: OrganizationClientRecordInvitationDetails;
    clientMedicalProfileId: string;
    clientOrganizerUserId: string;
    provisionedMedicalProfileId?: string;
};

export type OrganizationClientRecord = OrganizationClientRecordBaseFields &
    (
        | OrganizationClientRecordNotInvitedFields
        | OrganizationClientRecordArchivedFields
        | OrganizationClientRecordInvitedFields
        | OrganizationClientRecordInvitationRejectedFields
        | OrganizationClientRecordActiveFields
        | OrganizationClientRecordInactiveFields
    );

export type OrganizationClientRecordForStatus<T extends OrganizationClientRecordStatus> = Extract<
    OrganizationClientRecord,
    { status: T }
>;

export const organizationUsageMetricType = ['medical-profile-count', 'medical-profile-maximum'] as const;
export type OrganizationUsageMetricType = (typeof organizationUsageMetricType)[number];

export const organizationUsageIntervalType = ['day', 'month'] as const;
export type OrganizationUsageIntervalType = (typeof organizationUsageIntervalType)[number];

export type OrganizationUsageMetric = {
    organizationId: string;
    metricType: OrganizationUsageMetricType;
    intervalType: OrganizationUsageIntervalType;
    intervalDate: string;
    value: number;
    recordedDateTime: string;
};

export const organizationStripeAccountStatuses = ['active', 'restricted'] as const;
export type OrganizationStripeAccountStatus = (typeof organizationStripeAccountStatuses)[number];

export type OrganizationStripeAccount = {
    stripeAccountId: string;
    organizationId: string;
    status: OrganizationStripeAccountStatus;
    setupByUserId: string;
};

type OrganizationInvoiceBaseFields = {
    invoiceId: string;
    invoiceNumber: string;
    stripeCustomerId: string;
    amount: number;
    pdfUrl: string;
    createdDateTime: string;
    dueDateTime: string;
    description?: string;
};

export const organizationInvoiceStatuses = ['open', 'paid', 'void', 'refunded'] as const;
export type OrganizationInvoiceStatus = (typeof organizationInvoiceStatuses)[number];

type OrganizationInvoiceOpenFields = {
    status: 'open';
    paymentUrl: string;
};

type OrganizationInvoicePaidFields = {
    status: 'paid';
    paidDateTime: string;
    /**
     * Whether the invoice has been marked as paid by a member of the organization.
     */
    markedPaid: boolean;
};

type OrganizationInvoiceVoidFields = {
    status: 'void';
    voidDateTime: string;
};

type OrganizationInvoiceRefundedFields = {
    status: 'refunded';
};

export type OrganizationInvoice = OrganizationInvoiceBaseFields &
    (
        | OrganizationInvoiceOpenFields
        | OrganizationInvoicePaidFields
        | OrganizationInvoiceVoidFields
        | OrganizationInvoiceRefundedFields
    );

export type OrganizationInvoiceForStatus<T extends OrganizationInvoiceStatus> = Extract<
    OrganizationInvoice,
    { status: T }
>;

type OrganizationInvoiceItemFixedAmountDetails = {
    amountType: 'fixed';
    amount: number;
};

type OrganizationInvoiceItemUnitBasedAmountDetails = {
    amountType: 'unit-based';
    unitAmount: number;
    quantity: number;
};

export type OrganizationInvoiceItem = {
    invoiceItemId: string;
    stripeCustomerId: string;
    description: string;
    period?: {
        start: string;
        end: string;
    };
    amountDetails: OrganizationInvoiceItemFixedAmountDetails | OrganizationInvoiceItemUnitBasedAmountDetails;
};

export type OrganizationInvoiceItemAmountType = OrganizationInvoiceItem['amountDetails']['amountType'];
